/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import { LEGACY_OK, OK } from '../../../constants/platformStatusCodes';
import arClient from '../../../util/api_client';
import { normalizeUniversalDiscoveryRecordDetail } from '../discoveryRecordUtils';
import {
  scheduleSyncUserSelectionForFeedback,
  scheduleSyncUserSelectionForInfluencerIds,
} from './userSelectionSync';
import { UD_ENDPOINTS, DISCOVERY_IN_FLIGHT_CODE, isDiscoveryInFlight } from './discoveryRealtime';

export function formatRelativeTime(timestamp) {
  if (!timestamp) return '—';
  const then = new Date(timestamp);

  /* Relative-time formatting is currently disabled in favour of the absolute
  timestamp below; kept for reference.
  const now = new Date();
  const diffMs = now - then;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffSecs < 60) return `${diffSecs}s ago`;
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) {
    const remainingMins = diffMins % 60;
    return remainingMins > 0
      ? `${diffHours}h ${remainingMins}m ago`
      : `${diffHours}h ago`;
  }
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 30) return `${diffDays} days ago`; */
  return then.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const initialState = {
  universalDiscoveryRecords: [],
  universalDiscoveryListsLoading: true,
  universalDiscoveryRecordDetail: null,
  universalDiscoveryCheckStatus: [],
  recordDetailLoading: false,
  influencerReport: {},
  influencerReportLoading: false,
  // Live (Reverb) progress per list id: { [id]: { percent, count } }.
  discoveryProgress: {},
};

/**
 * Overlay a freshly-fetched detail with the current one's local like/dislike so a
 * live re-fetch (fired by a `discovery.results` event) never clobbers a selection
 * that is still mid-debounce in userSelectionSync, and never shrinks a non-empty
 * roster to empty (a first-wave/ariadne re-fetch can transiently return fewer rows
 * than a later merged one). The current detail already reflects the server's
 * selection for untouched rows, so only rows the user has toggled since the last
 * sync differ — including a de-selection (like/dislike toggled back off).
 */
function mergeDetailPreservingSelections(prevDetail, nextDetail) {
  if (!nextDetail || typeof nextDetail !== 'object') return nextDetail;
  if (!prevDetail || String(prevDetail.id) !== String(nextDetail.id)) return nextDetail;

  const prevInfluencers = Array.isArray(prevDetail.influencers) ? prevDetail.influencers : [];
  const nextInfluencers = Array.isArray(nextDetail.influencers) ? nextDetail.influencers : [];

  if (prevInfluencers.length > 0 && nextInfluencers.length === 0) {
    return { ...nextDetail, influencers: prevInfluencers };
  }
  if (prevInfluencers.length === 0 || nextInfluencers.length === 0) {
    return nextDetail;
  }

  const prevById = new Map(prevInfluencers.map((i) => [String(i.creatorReportDetailsId), i]));
  const influencers = nextInfluencers.map((inf) => {
    const prev = prevById.get(String(inf.creatorReportDetailsId));
    if (!prev) return inf;
    // prev === next for untouched rows (both reflect server state), so a difference
    // is an unsynced local toggle — keep it, including a de-selection back to neutral.
    if (prev.isLike === inf.isLike && prev.isDislike === inf.isDislike) return inf;
    return { ...inf, isLike: prev.isLike, isDislike: prev.isDislike };
  });
  return { ...nextDetail, influencers };
}

const UniversalDiscoverySlice = createSlice({
  name: 'UniversalDiscovery',
  initialState,
  reducers: {
    setUniversalDiscoveryRecords: (state, action) => {
      state.universalDiscoveryRecords = action.payload;
    },
    setUniversalDiscoveryRecordDetail: (state, action) => {
      state.universalDiscoveryRecordDetail = action.payload;
    },
    toggleFavoriteRecord: (state, action) => {
      const record = state.universalDiscoveryRecords.find((r) => r.id === action.payload);
      if (record) {
        record.isLiked = String(record.isLiked) === '1' ? '0' : '1';
      }
    },
    removeRecord: (state, action) => {
      state.universalDiscoveryRecords = state.universalDiscoveryRecords.filter(
        (r) => r.id !== action.payload
      );
    },
    likeInfluencer: (state, action) => {
      const influencer = state.universalDiscoveryRecordDetail?.influencers?.find(
        (i) => i.creatorReportDetailsId === action.payload
      );
      if (influencer) {
        influencer.isLike = !influencer.isLike;
        if (influencer.isLike) influencer.isDislike = false;
      }
    },
    dislikeInfluencer: (state, action) => {
      const influencer = state.universalDiscoveryRecordDetail?.influencers?.find(
        (i) => i.creatorReportDetailsId === action.payload
      );
      if (influencer) {
        influencer.isDislike = !influencer.isDislike;
        if (influencer.isDislike) influencer.isLike = false;
      }
    },
    bulkLikeInfluencers: (state, action) => {
      const idSet = new Set(action.payload);
      const influencers = state.universalDiscoveryRecordDetail?.influencers;
      if (influencers) {
        influencers.forEach((i) => {
          if (idSet.has(i.creatorReportDetailsId)) {
            i.isLike = true;
            i.isDislike = false;
          }
        });
      }
    },
    bulkDislikeInfluencers: (state, action) => {
      const idSet = new Set(action.payload);
      const influencers = state.universalDiscoveryRecordDetail?.influencers;
      if (influencers) {
        influencers.forEach((i) => {
          if (idSet.has(i.creatorReportDetailsId)) {
            i.isDislike = true;
            i.isLike = false;
          }
        });
      }
    },
    addChatMessage: (state, action) => {
      if (state.universalDiscoveryRecordDetail) {
        state.universalDiscoveryRecordDetail.chats.push(action.payload);
      }
    },
    setUniversalDiscoveryCheckStatus: (state, action) => {
      state.universalDiscoveryCheckStatus = Array.isArray(action.payload) ? action.payload : [];
    },
    setUniversalDiscoveryRecordDetailLoading: (state, action) => {
      state.recordDetailLoading = Boolean(action.payload);
    },
    setInfluencerReport: (state, action) => {
      state.influencerReport = action.payload;
    },
    setInfluencerReportLoading: (state, action) => {
      state.influencerReportLoading = action.payload;
    },
    setUniversalDiscoveryListsLoading: (state, action) => {
      state.universalDiscoveryListsLoading = Boolean(action.payload);
    },
    // Live progress from `.discovery.progress` / `.discovery.results`. Shallow-merge
    // so a percent-only event does not wipe count (and vice-versa). Percent is
    // monotonic — a redelivered/out-of-order lower value is ignored so the bar
    // never jumps backward.
    setDiscoveryProgress: (state, action) => {
      const { id, percent, count } = action.payload || {};
      if (id == null || id === '') return;
      const key = String(id);
      const prev = state.discoveryProgress[key] || {};
      const next = { ...prev };
      if (percent != null && Number.isFinite(Number(percent))) {
        const p = Number(percent);
        if (prev.percent == null || p >= Number(prev.percent)) {
          next.percent = p;
        }
      }
      if (count != null && Number.isFinite(Number(count))) {
        const c = Number(count);
        // Monotonic like percent — a redelivered/out-of-order results event must
        // not make the X/1500 counter regress.
        if (prev.count == null || c >= Number(prev.count)) {
          next.count = c;
        }
      }
      state.discoveryProgress[key] = next;
    },
    clearDiscoveryProgress: (state, action) => {
      delete state.discoveryProgress[String(action.payload)];
    },
    // Merge-by-id / unshift (mirrors Audiences upsertAudience). Ids are String()-
    // coerced on both sides because WS payloads are numeric while rows store
    // String(universalListId).
    upsertDiscoveryRecord: (state, action) => {
      const record = action.payload;
      if (!record || record.id == null) return;
      const idx = state.universalDiscoveryRecords.findIndex(
        (r) => String(r.id) === String(record.id)
      );
      if (idx >= 0) {
        state.universalDiscoveryRecords[idx] = {
          ...state.universalDiscoveryRecords[idx],
          ...record,
        };
      } else {
        state.universalDiscoveryRecords.unshift(record);
      }
    },
  },
});

export const {
  setUniversalDiscoveryRecords,
  setUniversalDiscoveryRecordDetail,
  toggleFavoriteRecord,
  removeRecord,
  likeInfluencer,
  dislikeInfluencer,
  bulkLikeInfluencers,
  bulkDislikeInfluencers,
  addChatMessage,
  setUniversalDiscoveryCheckStatus,
  setUniversalDiscoveryRecordDetailLoading,
  setUniversalDiscoveryListsLoading,
  setInfluencerReport,
  setInfluencerReportLoading,
  setDiscoveryProgress,
  clearDiscoveryProgress,
  upsertDiscoveryRecord,
} = UniversalDiscoverySlice.actions;

/**
 * createV2 returns job metadata, not a full fetchLists row — normalize for the UI.
 * Example: { universalListId, universalListCreatorRequestId, status, createdAt, updatedAt }
 */
function mapCreateV2DataToListRow(data, { keyword, filtersJson, isCustom }) {
  if (!data || typeof data !== 'object' || data.universalListId == null) {
    return null;
  }

  const s = String(data.status || '').toLowerCase();
  let uiStatus = 'inProgress';
  if (s === 'completed' || s === 'complete') uiStatus = 'completed';
  else if (s === 'failed' || s === 'error') uiStatus = 'failed';
  else if (s === 'pending') uiStatus = 'inProgress';

  const row = {
    id: String(data.universalListId),
    name: keyword,
    filterJson: filtersJson,
    status: uiStatus,
    createdTimeStamp: data.createdAt,
    createdTimestamp: data.createdAt,
    updatedAt: data.updatedAt,
    totalInfluencers: null,
    isLiked: '0',
    // The create response doesn't echo the flag back, so carry it over from the
    // request — otherwise a brand-new custom list shows under the AI filter
    // until the next fetchLists.
    isCustom: String(data.isCustom ?? isCustom ?? '0') === '1' ? '1' : '0',
    version: 'V2',
    socialNetwork: '',
    pageIndex: '0',
  };
  if (data.universalListCreatorRequestId != null) {
    row.universalListCreatorRequestId = String(data.universalListCreatorRequestId);
  }
  return row;
}

function normalizeCreateResponseToListRow(respData, { keyword, filtersJson, isCustom }) {
  if (respData == null) return null;

  const fromV2 = mapCreateV2DataToListRow(respData, { keyword, filtersJson, isCustom });
  if (fromV2) return fromV2;

  if (Array.isArray(respData)) {
    return respData[0] && typeof respData[0] === 'object' ? respData[0] : null;
  }

  return typeof respData === 'object' ? respData : null;
}

export const fetchUniversalDiscoveryRecords = () => async (dispatch) => {
  dispatch(setUniversalDiscoveryListsLoading(true));
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      try {
        if (resp.status === OK || resp.status === LEGACY_OK) {
          const raw = Array.isArray(resp.data) ? resp.data : [];
          const list = raw.filter((item) => item != null && String(item.version) === 'V2');
          dispatch(setUniversalDiscoveryRecords(list));
          resolve(true);
        } else {
          dispatch(setUniversalDiscoveryRecords([]));
          reject(new Error(resp?.message || 'Failed to load discovery lists'));
        }
      } finally {
        dispatch(setUniversalDiscoveryListsLoading(false));
      }
    };
    arClient.doApiCall('/universal-list/fetchLists', {}, callback);
  });
};

function checkStatusResponseHasInProgress(list) {
  return (Array.isArray(list) ? list : []).some((item) => {
    const u = String(item?.status || '').toUpperCase();
    return u === 'IN_PROGRESS' || u === 'PENDING';
  });
}

export const checkUniversalDiscoveryStatus = () => async (dispatch) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === OK || resp.status === LEGACY_OK) {
        const list = Array.isArray(resp.data) ? resp.data : [];
        dispatch(setUniversalDiscoveryCheckStatus(list));
        resolve(checkStatusResponseHasInProgress(list));
      } else {
        reject(new Error(resp?.message || 'checkStatus failed'));
      }
    };

    arClient.doApiCall('/universal-list/checkStatus', {}, callback);
  });
};

/**
 * Shared response handling for the create endpoints (createV2 / createCustomV2):
 * prepend the new list row, or fall back to a full refetch when the response
 * can't be mapped to one.
 */
function handleCreateListResponse(
  resp,
  { dispatch, existingRecords, rowMeta, endpoint, resolve, reject }
) {
  if (resp.status === OK || resp.status === LEGACY_OK) {
    const newRecord = normalizeCreateResponseToListRow(resp.data, rowMeta);
    if (newRecord) {
      dispatch(setUniversalDiscoveryRecords([newRecord, ...existingRecords]));
      resolve(true);
      return;
    }
    dispatch(fetchUniversalDiscoveryRecords())
      .then(() => resolve(true))
      .catch(() =>
        reject(
          new Error(
            resp?.message || 'Universal Discovery create succeeded but no list row was returned'
          )
        )
      );
  } else if (isDiscoveryInFlight(resp)) {
    // State C: a discovery is already running for this workspace (HTTP 409).
    // Surface a distinct code so the caller can show an informational toast
    // instead of a generic failure — no list row is added.
    const err = new Error('A discovery is already running.');
    err.code = DISCOVERY_IN_FLIGHT_CODE;
    reject(err);
  } else {
    // eslint-disable-next-line no-console
    console.error(`${endpoint} failed`, resp);
    reject(new Error(resp?.message || 'Universal Discovery create failed'));
  }
}

/** Create an AI-search discovery list (POST /universal-list/createV2). */
export const createUDRecord = (keyword, filters) => async (dispatch, getState) => {
  const { UniversalDiscovery } = getState();
  return new Promise((resolve, reject) => {
    let filtersJson = '{}';
    if (filters != null) {
      filtersJson = typeof filters === 'string' ? filters : JSON.stringify(filters);
    }

    const callback = (resp) =>
      handleCreateListResponse(resp, {
        dispatch,
        existingRecords: UniversalDiscovery.universalDiscoveryRecords,
        rowMeta: { keyword, filtersJson },
        endpoint: 'universal-list/createV2',
        resolve,
        reject,
      });

    arClient.doApiCall(
      '/universal-list/createV2',
      {
        pageIndex: 0,
        name: keyword,
        filtersJson,
      },
      callback
    );
  });
};

/**
 * Create a custom discovery list (POST /universal-list/createCustomV2) from
 * hand-picked profiles. `infURLArray` is the `{ network: [handle, ...] }` map
 * from buildInfluencerNetworkMap; it goes over the wire JSON-encoded because
 * doApiCall form-encodes each value.
 */
export const createCustomUDRecord = (name, infURLArray) => async (dispatch, getState) => {
  const { UniversalDiscovery } = getState();
  return new Promise((resolve, reject) => {
    const callback = (resp) =>
      handleCreateListResponse(resp, {
        dispatch,
        existingRecords: UniversalDiscovery.universalDiscoveryRecords,
        rowMeta: { keyword: name, filtersJson: '{}', isCustom: 1 },
        endpoint: 'universal-list/createCustomV2',
        resolve,
        reject,
      });

    arClient.doApiCall(
      '/universal-list/createCustomV2',
      {
        name,
        infURLArray: JSON.stringify(infURLArray),
      },
      callback
    );
  });
};

export const fetchUniversalDiscoveryRecordDetail = (udId, { silent = false } = {}) => async (
  dispatch,
  getState
) => {
  // Live re-fetches (fired by `.discovery.results`) pass silent:true so the detail
  // loader does not flash while the roster streams in.
  if (!silent) dispatch(setUniversalDiscoveryRecordDetailLoading(true));
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === OK || resp.status === LEGACY_OK) {
        const prev = getState().UniversalDiscovery.universalDiscoveryRecordDetail;
        const next = normalizeUniversalDiscoveryRecordDetail(resp.data);
        dispatch(setUniversalDiscoveryRecordDetail(mergeDetailPreservingSelections(prev, next)));
        if (!silent) dispatch(setUniversalDiscoveryRecordDetailLoading(false));
        resolve(true);
      } else {
        if (!silent) dispatch(setUniversalDiscoveryRecordDetailLoading(false));
        reject(new Error(resp?.message || 'Failed to load discovery detail'));
      }
    };

    arClient.doApiCall('/universal-list/fetchListById', { universalListId: udId }, callback);
  });
};

export const likeInfluencerWithSelectionSync = (creatorReportDetailsId) => (dispatch, getState) => {
  dispatch(likeInfluencer(creatorReportDetailsId));
  const inf = getState().UniversalDiscovery.universalDiscoveryRecordDetail?.influencers?.find(
    (i) => String(i.creatorReportDetailsId) === String(creatorReportDetailsId)
  );
  if (inf?.universalListCreatorFeedbackId == null) return;
  scheduleSyncUserSelectionForFeedback(
    inf.universalListCreatorFeedbackId,
    () => getState().UniversalDiscovery.universalDiscoveryRecordDetail
  );
};

export const dislikeInfluencerWithSelectionSync = (creatorReportDetailsId) => (
  dispatch,
  getState
) => {
  dispatch(dislikeInfluencer(creatorReportDetailsId));
  const inf = getState().UniversalDiscovery.universalDiscoveryRecordDetail?.influencers?.find(
    (i) => String(i.creatorReportDetailsId) === String(creatorReportDetailsId)
  );
  if (inf?.universalListCreatorFeedbackId == null) return;
  scheduleSyncUserSelectionForFeedback(
    inf.universalListCreatorFeedbackId,
    () => getState().UniversalDiscovery.universalDiscoveryRecordDetail
  );
};

export const bulkLikeInfluencersWithSelectionSync = (ids) => (dispatch, getState) => {
  dispatch(bulkLikeInfluencers(ids));
  scheduleSyncUserSelectionForInfluencerIds(
    () => getState().UniversalDiscovery.universalDiscoveryRecordDetail,
    ids
  );
};

export const bulkDislikeInfluencersWithSelectionSync = (ids) => (dispatch, getState) => {
  dispatch(bulkDislikeInfluencers(ids));
  scheduleSyncUserSelectionForInfluencerIds(
    () => getState().UniversalDiscovery.universalDiscoveryRecordDetail,
    ids
  );
};

export const expandUniversalDiscoveryList = (feedback) => async (dispatch, getState) => {
  const detail = getState()?.UniversalDiscovery?.universalDiscoveryRecordDetail;
  const universalListId = detail?.id;
  if (universalListId == null || universalListId === '') {
    return Promise.reject(new Error('universalListId is required'));
  }

  const influencers = Array.isArray(detail?.influencers) ? detail.influencers : [];
  const userSelectionMap = {};
  influencers.forEach((inf) => {
    const username = inf?.username;
    if (!username) return;
    if (inf.isLike === true) {
      userSelectionMap[username] = 1;
      return;
    }
    if (inf.isDislike === true) {
      userSelectionMap[username] = 0;
    }
  });

  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === OK || resp.status === LEGACY_OK) {
        dispatch(fetchUniversalDiscoveryRecordDetail(universalListId))
          .then(() => resolve(resp?.data ?? true))
          .catch(() =>
            reject(new Error('expandList succeeded but failed to refresh discovery detail'))
          );
      } else {
        reject(new Error(resp?.message || 'Failed to expand discovery list'));
      }
    };

    arClient.doApiCall(
      '/universal-list/expandList',
      {
        universalListId: String(universalListId),
        userSelection: JSON.stringify(userSelectionMap),
        feedback: feedback ?? '', // JSON.stringify(feedback ?? ''),
      },
      callback
    );
  });
};

export const toggleFavoriteUniversalDiscoveryRecord = (udId) => async (dispatch, getState) => {
  const { UniversalDiscovery } = getState();
  const record = UniversalDiscovery.universalDiscoveryRecords.find(
    (r) => String(r.id) === String(udId)
  );
  if (!record) {
    return Promise.reject(new Error('Discovery list not found'));
  }

  const currentlyLiked = String(record.isLiked) === '1';
  const isLiked = currentlyLiked ? '0' : '1';

  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === OK || resp.status === LEGACY_OK) {
        dispatch(toggleFavoriteRecord(udId));
        resolve(true);
      } else {
        reject(new Error(resp?.message || 'Failed to update favorite'));
      }
    };

    arClient.doApiCall(
      '/universal-list/setFavorite',
      {
        universalListId: String(udId),
        isLiked,
      },
      callback
    );
  });
};

export const deleteUniversalDiscoveryRecord = (udId) => async (dispatch) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === OK || resp.status === LEGACY_OK) {
        dispatch(removeRecord(udId));
        resolve(true);
      } else {
        reject(new Error(resp?.message || 'Failed to delete discovery'));
      }
    };

    arClient.doApiCall(
      '/universal-list/deleteSavedList',
      {
        universalListId: udId,
      },
      callback
    );
  });
};

/**
 * Cancel an in-flight discovery (POST /universalList/cancel-discovery). The server
 * stops the search and broadcasts `discovery.results { source:'cancelled', done:true }`
 * (which the live listener treats as terminal). We also refresh status/list here so
 * the card reflects the terminal state immediately even when the socket is down;
 * on failure we reject so the caller can show an informational toast.
 */
export const cancelUniversalDiscovery = (universalListId) => async (dispatch) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === OK || resp.status === LEGACY_OK) {
        dispatch(checkUniversalDiscoveryStatus()).catch(() => {});
        dispatch(fetchUniversalDiscoveryRecords()).catch(() => {});
        resolve(true);
      } else {
        reject(new Error(resp?.message || 'Cancel is not available yet'));
      }
    };

    arClient.doApiCall(UD_ENDPOINTS.CANCEL, { universalListId: String(universalListId) }, callback);
  });
};

/**
 * Rerun a discovery (POST /universalList/rerun-discovery): the server cancels any
 * running search and re-runs fresh on the same list, then emits the usual
 * progress -> ariadne -> merged wave events. On success we refresh status/list so
 * the row flips back to in-progress (which re-mounts its live listener). If the
 * endpoint is unavailable we fall back to a fresh createV2 with the original
 * keyword/filters so rerun still works.
 */
export const rerunUniversalDiscovery = (universalListId) => async (dispatch, getState) => {
  const record = getState().UniversalDiscovery.universalDiscoveryRecords.find(
    (r) => String(r.id) === String(universalListId)
  );

  return new Promise((resolve, reject) => {
    const runViaCreate = () => {
      if (!record) {
        reject(new Error('Rerun is not available for this discovery'));
        return;
      }
      dispatch(createUDRecord(record.name ?? '', record.filterJson ?? '{}'))
        .then(() => resolve(true))
        .catch((err) => reject(err));
    };

    const callback = (resp) => {
      if (resp.status === OK || resp.status === LEGACY_OK) {
        dispatch(checkUniversalDiscoveryStatus()).catch(() => {});
        dispatch(fetchUniversalDiscoveryRecords()).catch(() => {});
        resolve(true);
      } else {
        // endpoint not available -> functional rerun via a fresh createV2
        runViaCreate();
      }
    };

    arClient.doApiCall(UD_ENDPOINTS.RERUN, { universalListId: String(universalListId) }, callback);
  });
};

export const getCreatorReport = (universalListId, username, socialNetwork) => async (dispatch) => {
  const callback = (respApi) => {
    if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
      let report = {};
      try {
        report =
          respApi?.data && respApi?.data?.normalizedRespData
            ? JSON.parse(respApi?.data?.normalizedRespData)
            : [];

        if (respApi?.data?.rationale) {
          report.rationale = respApi.data.rationale;
        } else {
          report.rationale = null;
        }

        const agInfluencer = respApi?.data?.agInfluencer;
        report.score = agInfluencer?.score ?? null;
        const { scoreBreakdown } = agInfluencer || {};
        report.scoreBreakdown =
          typeof scoreBreakdown === 'string' ? JSON.parse(scoreBreakdown) : scoreBreakdown || null;
        report.lastPulledAt = respApi?.data?.lastPulledAt ?? null;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('invalid JSON string', error);
      }

      dispatch(setInfluencerReport(report));
    } else {
      dispatch(setInfluencerReport({}));
      // eslint-disable-next-line no-console
      console.error('get report failed', respApi);
    }
    dispatch(setInfluencerReportLoading(false));
  };
  dispatch(setInfluencerReportLoading(true));

  arClient.doApiCall(
    '/universal-list/getCreatorReport',
    { universalListId, username, socialNetwork },
    callback
  );
};

export default UniversalDiscoverySlice.reducer;
